import { ApolloClient, InMemoryCache, HttpLink, from, concat, split, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { getRefreshToken as fetchRefreshToken, getTokenMetadata } from 'Api/auth';
import {
  getRefreshToken,
  isExpired,
  setLoading,
  logout,
  getAuthStore,
  setExpirationDate,
  setAuthStore,
  setUserId,
} from 'Graphql/store';

const httpLink = new HttpLink({ uri: process.env.API_GATEWAY });
const fragariaLink = new HttpLink({ uri: process.env.FRAGARIA_GATEWAY });

const FRAGARIA_FIELDS = new Set([
  'getDevice',
  'getDevices',
  'getCarDevice',
  'getCarDeviceByDeviceId',
  'getCarDevices',
  'getDeviceConfigs',
  'createDevice',
  'executeCommand',
  'updateDevice',
  'addCommandToDeviceConfig',
  'telematicsRealTimeMetrics',
]);

const isFragariaOperation = ({ query }) =>
  query.definitions.some(
    (def) =>
      def.kind === 'OperationDefinition' &&
      def.selectionSet.selections.some((sel) => {
        const name = sel.name?.value || '';
        return FRAGARIA_FIELDS.has(name);
      }),
  );

const splitLink = split(isFragariaOperation, fragariaLink, httpLink);

const handleSuccessfulRefresh = ({ data }) => {
  setAuthStore(data);
  setLoading(false);
};

const handleError = () => {
  logout();
  setLoading(false);
};

const refreshQuery = async (currentRefreshToken) => {
  return fetchRefreshToken(
    { refresh_token: currentRefreshToken },
    { onSuccess: handleSuccessfulRefresh, onError: handleError },
  );
};

const getAccessToken = async (currentRefreshToken, errorOrigin) => {
  const authStoreData = getAuthStore();
  if ((currentRefreshToken && isExpired()) || errorOrigin) {
    try {
      setExpirationDate();
      const { data: refreshQueryData } = (await refreshQuery(currentRefreshToken)) || {};
      const { access_token: accessToken } = refreshQueryData || {};
      if (!authStoreData?.userId)
        await getTokenMetadata(accessToken, { onSuccess: ({ data }) => setUserId(data?.user_id) });
      return { accessToken: accessToken || false };
    } catch (error) {
      logout();
      setLoading(false);
      return { accessToken: false };
    }
  }
  setLoading(false);
  return authStoreData;
};

const authMiddleware = setContext(async (_, { headers, forRefreshToken }) => {
  const currentRefreshToken = getRefreshToken();
  const { accessToken } = await getAccessToken(currentRefreshToken);
  return {
    headers: {
      ...headers,
      Authorization: accessToken && !forRefreshToken ? `Bearer ${accessToken}` : '',
    },
  };
});

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'UNAUTHENTICATED') {
        return new Observable((observer) => {
          getAccessToken(getRefreshToken(), true)
            .then(({ accessToken }) => {
              if (!accessToken) {
                return observer.complete();
              }
              const { headers: oldHeaders } = operation.getContext();
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  Authorization: `Bearer ${accessToken}`,
                },
              });
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            })
            .catch((error) => {
              observer.error(error);
            });
        });
      }
    }
  }
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, concat(authMiddleware, splitLink)]),
});

export default client;
